import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { formatDate, formatNumber, capitalize, debounce, getPlanColor } from '../utils/helpers';
import {
  Plug, Key, Users, Database, Search, RefreshCw, Shield,
  XCircle, ChevronDown, ChevronRight, AlertTriangle
} from 'lucide-react';

const StatCard = ({ title, value, icon: Icon, color }) => (
  <div className="card">
    <div className="card-body">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-semibold mt-1">{formatNumber(value)}</p>
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  </div>
);

const RevokeModal = ({ user, onConfirm, onClose, loading }) => (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div className="bg-white rounded-lg max-w-md w-full">
      <div className="p-6">
        <div className="flex items-center mb-4">
          <div className="p-2 bg-red-100 rounded-lg mr-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900">Revoke MCP Access</h3>
        </div>
        <p className="text-sm text-gray-600 mb-2">
          Revoke all MCP tokens for <strong>{user.userEmail}</strong>?
        </p>
        <p className="text-sm text-gray-500 mb-6">
          This will disconnect their Claude/ChatGPT integrations immediately.
          They will need to re-authorize to regain access.
        </p>
        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="btn btn-secondary" disabled={loading}>Cancel</button>
          <button onClick={onConfirm} className="btn btn-danger" disabled={loading}>
            {loading ? 'Revoking...' : 'Revoke All Tokens'}
          </button>
        </div>
      </div>
    </div>
  </div>
);

const RevokeTokenModal = ({ token, onConfirm, onClose, loading }) => (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div className="bg-white rounded-lg max-w-md w-full">
      <div className="p-6">
        <div className="flex items-center mb-4">
          <div className="p-2 bg-red-100 rounded-lg mr-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900">Revoke Token</h3>
        </div>
        <p className="text-sm text-gray-600 mb-2">
          Revoke this <strong>{token.clientName}</strong> token?
        </p>
        <p className="text-sm text-gray-500 mb-6">
          This will disconnect this specific integration. Other tokens for this user will remain active.
        </p>
        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="btn btn-secondary" disabled={loading}>Cancel</button>
          <button onClick={onConfirm} className="btn btn-danger" disabled={loading}>
            {loading ? 'Revoking...' : 'Revoke Token'}
          </button>
        </div>
      </div>
    </div>
  </div>
);

const TokenStatusBadge = ({ status }) => {
  const classes = {
    active: 'badge-success',
    revoked: 'badge-danger',
    expired: 'badge-warning'
  };
  return <span className={`badge ${classes[status] || 'badge-gray'}`}>{capitalize(status)}</span>;
};

const PlanBadge = ({ plan }) => (
  <span className={`font-medium ${getPlanColor(plan)}`}>{capitalize(plan)}</span>
);

export default function Connectors() {
  const [activeTab, setActiveTab] = useState('users');
  const [stats, setStats] = useState({});
  const [connectorUsers, setConnectorUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [selectedToken, setSelectedToken] = useState(null);
  const [showRevokeTokenModal, setShowRevokeTokenModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [userDetail, setUserDetail] = useState(null);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/connectors/stats');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Fetch connector stats error:', error);
    }
  };

  const fetchConnectorUsers = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: page.toString(), limit: '20' });
      if (search.trim()) params.append('search', search.trim());
      if (statusFilter) params.append('status', statusFilter);

      const response = await api.get(`/admin/connectors/users?${params}`);
      setConnectorUsers(response.data.users);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Fetch connector users error:', error);
      toast.error('Failed to load connector users');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  const fetchClients = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/connectors/clients?page=${page}&limit=20`);
      setClients(response.data.clients);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Fetch clients error:', error);
      toast.error('Failed to load OAuth clients');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUserDetail = async (userId) => {
    try {
      const response = await api.get(`/admin/connectors/users/${userId}`);
      setUserDetail(response.data);
    } catch (error) {
      console.error('Fetch user detail error:', error);
      toast.error('Failed to load user details');
    }
  };

  const handleRevoke = async () => {
    try {
      setActionLoading(true);
      const response = await api.post(`/admin/connectors/users/${selectedUser.userId}/revoke`);
      toast.success(response.data.message);
      setShowRevokeModal(false);
      setSelectedUser(null);
      setExpandedUserId(null);
      setUserDetail(null);
      fetchConnectorUsers(pagination.currentPage);
      fetchStats();
    } catch (error) {
      console.error('Revoke error:', error);
      toast.error(error.response?.data?.message || 'Failed to revoke tokens');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeToken = async () => {
    try {
      setActionLoading(true);
      const response = await api.post(`/admin/connectors/tokens/${selectedToken._id}/revoke`);
      toast.success(response.data.message);
      setShowRevokeTokenModal(false);
      setSelectedToken(null);
      if (expandedUserId) {
        await fetchUserDetail(expandedUserId);
      }
      fetchConnectorUsers(pagination.currentPage);
      fetchStats();
    } catch (error) {
      console.error('Revoke token error:', error);
      toast.error(error.response?.data?.message || 'Failed to revoke token');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExpandRow = async (userId) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      setUserDetail(null);
    } else {
      setExpandedUserId(userId);
      await fetchUserDetail(userId);
    }
  };

  const debouncedSearch = useCallback(debounce(() => {
    if (activeTab === 'users') fetchConnectorUsers(1);
  }, 500), [fetchConnectorUsers, activeTab]);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchConnectorUsers(1);
    } else {
      fetchClients(1);
    }
    setExpandedUserId(null);
    setUserDetail(null);
  }, [activeTab, statusFilter]);

  useEffect(() => {
    debouncedSearch();
  }, [search]);

  const handleRefresh = () => {
    fetchStats();
    if (activeTab === 'users') fetchConnectorUsers(pagination.currentPage || 1);
    else fetchClients(pagination.currentPage || 1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">MCP Connectors</h1>
          <p className="text-sm text-gray-500 mt-1">Manage connected MCP clients and OAuth tokens</p>
        </div>
        <button onClick={handleRefresh} className="btn btn-secondary">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Connected Users" value={stats.connectedUsers} icon={Users} color="bg-blue-500" />
        <StatCard title="Active Tokens" value={stats.activeTokens} icon={Key} color="bg-green-500" />
        <StatCard title="Registered Clients" value={stats.registeredClients} icon={Shield} color="bg-purple-500" />
        <StatCard title="Cache Entries" value={stats.cacheEntries} icon={Database} color="bg-orange-500" />
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {['users', 'clients'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-3 px-1 border-b-2 text-sm font-medium ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab === 'users' ? 'Connected Users' : 'OAuth Clients'}
            </button>
          ))}
        </nav>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <>
          {/* Filters */}
          <div className="card">
            <div className="card-body">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    className="input pl-10"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <select
                  className="input w-full sm:w-48"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="revoked">Revoked / Expired</option>
                </select>
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="card">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th className="w-8"></th>
                    <th>User</th>
                    <th>Plan</th>
                    <th>Clients</th>
                    <th>Tokens</th>
                    <th>Last Connected</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="7" className="text-center py-8"><div className="loading-spinner mx-auto"></div></td></tr>
                  ) : connectorUsers.length === 0 ? (
                    <tr><td colSpan="7" className="text-center py-8 text-gray-500">No connector users found</td></tr>
                  ) : connectorUsers.map(user => (
                    <React.Fragment key={user.userId}>
                      <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => handleExpandRow(user.userId)}>
                        <td>
                          {expandedUserId === user.userId
                            ? <ChevronDown className="w-4 h-4 text-gray-400" />
                            : <ChevronRight className="w-4 h-4 text-gray-400" />}
                        </td>
                        <td>
                          <div>
                            <p className="font-medium text-gray-900">{user.userName || 'N/A'}</p>
                            <p className="text-sm text-gray-500">{user.userEmail}</p>
                          </div>
                        </td>
                        <td><PlanBadge plan={user.userPlan} /></td>
                        <td>
                          <div className="text-sm">
                            {user.clients.map(c => c.clientName).join(', ')}
                          </div>
                        </td>
                        <td>
                          <span className="text-green-600 font-medium">{user.activeTokens}</span>
                          <span className="text-gray-400"> / {user.totalTokens}</span>
                        </td>
                        <td className="text-sm text-gray-500">{formatDate(user.lastTokenCreated)}</td>
                        <td>
                          {user.activeTokens > 0 && (
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedUser(user);
                                setShowRevokeModal(true);
                              }}
                            >
                              <XCircle className="w-3 h-3 mr-1" />
                              Revoke All
                            </button>
                          )}
                        </td>
                      </tr>
                      {/* Expanded detail row */}
                      {expandedUserId === user.userId && (
                        <tr>
                          <td colSpan="7" className="bg-gray-50 px-6 py-4">
                            {!userDetail ? (
                              <div className="flex justify-center py-4"><div className="loading-spinner"></div></div>
                            ) : (
                              <div>
                                <div className="flex items-center gap-4 mb-3">
                                  <span className="text-sm font-medium text-gray-700">
                                    Active: {userDetail.connectorStats.activeTokens}
                                  </span>
                                  <span className="text-sm text-gray-500">
                                    Revoked: {userDetail.connectorStats.revokedTokens}
                                  </span>
                                  <span className="text-sm text-gray-500">
                                    Expired: {userDetail.connectorStats.expiredTokens}
                                  </span>
                                </div>
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="text-left text-gray-500 border-b">
                                      <th className="pb-2 font-medium">Client</th>
                                      <th className="pb-2 font-medium">Status</th>
                                      <th className="pb-2 font-medium">Access Expires</th>
                                      <th className="pb-2 font-medium">Refresh Expires</th>
                                      <th className="pb-2 font-medium">Created</th>
                                      <th className="pb-2 font-medium">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {userDetail.tokens.map(token => (
                                      <tr key={token._id} className="border-b border-gray-100">
                                        <td className="py-2">{token.clientName}</td>
                                        <td className="py-2"><TokenStatusBadge status={token.status} /></td>
                                        <td className="py-2 text-gray-500">{formatDate(token.accessTokenExpiresAt)}</td>
                                        <td className="py-2 text-gray-500">{formatDate(token.refreshTokenExpiresAt)}</td>
                                        <td className="py-2 text-gray-500">{formatDate(token.createdAt)}</td>
                                        <td className="py-2">
                                          {token.status === 'active' && (
                                            <button
                                              className="btn btn-sm btn-danger"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedToken(token);
                                                setShowRevokeTokenModal(true);
                                              }}
                                            >
                                              <XCircle className="w-3 h-3 mr-1" />
                                              Revoke
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="card-footer flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Page {pagination.currentPage} of {pagination.totalPages} ({pagination.total} total)
                </p>
                <div className="flex space-x-2">
                  <button
                    className="btn btn-sm btn-secondary"
                    disabled={!pagination.hasPrevPage}
                    onClick={() => fetchConnectorUsers(pagination.currentPage - 1)}
                  >
                    Previous
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    disabled={!pagination.hasNextPage}
                    onClick={() => fetchConnectorUsers(pagination.currentPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Clients Tab */}
      {activeTab === 'clients' && (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Client Name</th>
                  <th>Client ID</th>
                  <th>Scope</th>
                  <th>Users</th>
                  <th>Active Tokens</th>
                  <th>Registered</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="text-center py-8"><div className="loading-spinner mx-auto"></div></td></tr>
                ) : clients.length === 0 ? (
                  <tr><td colSpan="6" className="text-center py-8 text-gray-500">No OAuth clients registered</td></tr>
                ) : clients.map(client => (
                  <tr key={client.clientId} className="hover:bg-gray-50">
                    <td className="font-medium text-gray-900">{client.clientName}</td>
                    <td>
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                        {client.clientId.substring(0, 16)}...
                      </code>
                    </td>
                    <td className="text-sm text-gray-500">{client.scope || 'mcp:access'}</td>
                    <td>{client.tokenStats.uniqueUsers}</td>
                    <td>
                      <span className="text-green-600 font-medium">{client.tokenStats.activeTokens}</span>
                      <span className="text-gray-400"> / {client.tokenStats.totalTokens}</span>
                    </td>
                    <td className="text-sm text-gray-500">{formatDate(client.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="card-footer flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Page {pagination.currentPage} of {pagination.totalPages} ({pagination.total} total)
              </p>
              <div className="flex space-x-2">
                <button
                  className="btn btn-sm btn-secondary"
                  disabled={!pagination.hasPrevPage}
                  onClick={() => fetchClients(pagination.currentPage - 1)}
                >
                  Previous
                </button>
                <button
                  className="btn btn-sm btn-secondary"
                  disabled={!pagination.hasNextPage}
                  onClick={() => fetchClients(pagination.currentPage + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Revoke Modal */}
      {showRevokeModal && selectedUser && (
        <RevokeModal
          user={selectedUser}
          onConfirm={handleRevoke}
          onClose={() => { setShowRevokeModal(false); setSelectedUser(null); }}
          loading={actionLoading}
        />
      )}

      {/* Revoke Single Token Modal */}
      {showRevokeTokenModal && selectedToken && (
        <RevokeTokenModal
          token={selectedToken}
          onConfirm={handleRevokeToken}
          onClose={() => { setShowRevokeTokenModal(false); setSelectedToken(null); }}
          loading={actionLoading}
        />
      )}
    </div>
  );
}
